import React, { PropsWithChildren } from "react";
import { Box } from "arkh";
import { BaseWidgetProps } from "../BaseProps";
import { StyleSheet } from "react-native";
import { WidgetTeamScheduleTimeLabel } from "./WidgetTeamScheduleTimeLabel";
import { WidgetTeamScheduleRoutineOccasionListItem } from "./WidgetTeamScheduleRoutineOccasionListItem";
import { WidgetTeamScheduleTimeLabelLayout } from "./WidgetTeamScheduleTimeLabelLayout";

import { useFragment, graphql } from "react-relay";
import Color from "color";
import { WidgetTeamScheduleListItem_item$key } from "./__generated__/WidgetTeamScheduleListItem_item.graphql";
import { WidgetOverviewSize } from "./__generated__/WidgetTeamSchedule_widget.graphql";
import { WidgetTeamScheduleInlineRoutineListItem } from "./WidgetTeamScheduleInlineRoutineListItem";
import { CalendarEventItemListItem } from "./CalendarEventItemListItem";
import { RoutineScheduleType } from "./__generated__/WidgetTeamScheduleContentSectionHeader_teamSchedule.graphql";

export const hasBorder = false;
export const WidgetTeamScheduleListItem = React.memo(
  WidgetTeamScheduleListItemC
);

function Container(
  props: PropsWithChildren<{
    tint: string;
    status: "active" | "soon" | "none";
    height: number;
  }>
) {
  return (
    <Box
      style={{
        flexDirection: "row",
        alignItems: "center",
        borderBottomColor: "rgba(0,0,0,0.05)",
        borderBottomWidth: StyleSheet.hairlineWidth,
        justifyContent: "center",
        backgroundColor:
          props.status === "active"
            ? Color(props.tint).lightness(94).toString()
            : undefined,
        height: props.height,
      }}
    >
      {props.children}
    </Box>
  );
}

function WidgetTeamScheduleListItemC(props: {
  isLast: boolean;
  tint: string;
  height: number;
  width: number;
  size: WidgetOverviewSize;
  item: WidgetTeamScheduleListItem_item$key;

  onPressRoutineOccasion?(routineId: string, sequenceNumber: number): void;

  status: "active" | "soon" | "none";
  isDarkTint: boolean;
  freq: RoutineScheduleType;
}) {
  const item = useFragment(
    graphql`
      fragment WidgetTeamScheduleListItem_item on TeamScheduleEventOccasion {
        ... on TeamScheduleInlineRoutineOccasion {
          id
          startAt
          taskExecution {
            routineOccasion {
              deadlineAt
              sequenceNumber
            }
            routine {
              id
            }
          }
          __typename
          ...WidgetTeamScheduleInlineRoutineListItem_TeamScheduleInlineRoutineOccasion
        }
        ... on RoutineOccasion {
          id
          __typename
          startAt
          deadlineAt
          sequenceNumber
          routine {
            id
          }
          ...WidgetTeamScheduleRoutineOccasionListItem_item
        }

        ... on TeamScheduleOneOffEvent {
          startAt
          endAt
          ...CalendarEventItemListItem_eventItem
          __typename
        }
      }
    `,
    props.item
  );
  const size =
    props.size === "TWO_X_ONE"
      ? "COMPACT"
      : props.size === "TWO_X_TWO"
      ? "DEFAULT"
      : "LARGE";

  switch (item.__typename) {
    case "TeamScheduleInlineRoutineOccasion":
      return (
        <Container
          height={props.height}
          status={props.status}
          tint={props.tint}
        >
          {props.freq === "DAILY" ? (
            <WidgetTeamScheduleTimeLabel
              status={props.status}
              time={item.startAt}
              isDarkTint={props.isDarkTint}
              tint={props.tint}
              endTime={
                item.taskExecution.routineOccasion.deadlineAt ?? undefined
              }
            />
          ) : (
            <Box spaceLeft="medium" />
          )}
          <WidgetTeamScheduleInlineRoutineListItem
            status={props.status}
            size={size}
            onPress={props.onPressRoutineOccasion}
            inlineRoutineOccasion={item}
            freq={props.freq}
          />
        </Container>
      );

    case "RoutineOccasion":
      return (
        <Container
          height={props.height}
          status={props.status}
          tint={props.tint}
        >
          {props.freq === "DAILY" ? (
            <WidgetTeamScheduleTimeLabel
              status={props.status}
              time={item.startAt}
              isDarkTint={props.isDarkTint}
              tint={props.tint}
              endTime={item.deadlineAt ?? undefined}
            />
          ) : (
            <Box spaceLeft="medium" />
          )}
          <WidgetTeamScheduleRoutineOccasionListItem
            tint={props.tint}
            status={props.status}
            size={size}
            freq={props.freq}
            onPress={props.onPressRoutineOccasion}
            item={item}
          />
        </Container>
      );

    case "TeamScheduleOneOffEvent":
      return (
        <Container
          height={props.height}
          status={props.status}
          tint={props.tint}
        >
          <WidgetTeamScheduleTimeLabel
            status={props.status}
            time={item.startAt}
            isDarkTint={props.isDarkTint}
            tint={props.tint}
            endTime={item.endAt ?? undefined}
          />
          <CalendarEventItemListItem
            eventItem={item}
            isDarkTint={props.isDarkTint}
            size="TWO_X_FOUR"
            width={props.width - WidgetTeamScheduleTimeLabelLayout.width}
            status={props.status}
          />
        </Container>
      );

    default:
      return null;
  }
}
